import * as React from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useRoute } from 'wouter';
import DynamicTable from '../DynamicTable';
import { IDynamicTableAddButton } from '../../interfaces/tables';
import { TABLE_AUTO_REPORT, TABLE_AUTO_REPORT_LOG } from '../../Services/TableService';
import { URL_AUTO_REPORTS } from '../../router/constants';
import TabComponent from '../TabComponent';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import OnlineHelp from '../OnlineHelp';
import AllForms from './AllForms';
import useAnalytics from '../../hooks/useAnalytics';
import { tabsByPermission } from './AutoReportService';

function AutoReport(): JSX.Element {
  const { formatMessage: f } = useIntl();
  const [pathname, navigate] = useLocation();
  const [, params] = useRoute(`${URL_AUTO_REPORTS}/edit/:id`);
  const [isSubpageOpened, setIsSubpageOpened] = React.useState<boolean>(false);
  const [callTable, setCallTable] = React.useState<boolean>(false);
  const [itemId, setItemId] = React.useState<number | null>(null);
  const tabs = React.useState(
    tabsByPermission([TABLE_AUTO_REPORT, TABLE_AUTO_REPORT_LOG], TABLE_AUTO_REPORT)
  )[0];
  const { setDocumentTitle } = useDocumentTitle();
  const { trackEvent } = useAnalytics();

  const handleClose = React.useCallback(() => {
    navigate(URL_AUTO_REPORTS);
    setIsSubpageOpened(false);
    setItemId(null);
    trackEvent(['auto reports', 'click', 'Autoreports close']);
  }, [navigate, trackEvent]);

  const updateTable = () => {
    setCallTable(true);
  };

  const handleModal = (id: number, isEdited: boolean) => {
    if (isEdited && id) {
      setIsSubpageOpened(true);
      setItemId(id);
      setCallTable(false);
      trackEvent(['auto reports', 'click', 'Autoreports edit open']);
    } else {
      trackEvent(['auto reports', 'click', 'Autoreports add open']);
      setIsSubpageOpened(true);
      setCallTable(false);
      setItemId(null);
    }
  };

  const buttons: IDynamicTableAddButton = {
    [TABLE_AUTO_REPORT]: {
      open: ['addBtn'],
    },
  };

  React.useEffect(() => {
    if (pathname === URL_AUTO_REPORTS) {
      setIsSubpageOpened(false);
      setItemId(null);
      const documentTitle = `${f({ id: 'tab.auto-report' })} - ${f({
        id: 'menu.autoReports',
      })} - ${f({ id: 'menu.reports' })}`;
      setDocumentTitle(documentTitle);
    } else if (pathname === `${URL_AUTO_REPORTS}/add`) {
      setIsSubpageOpened(true);
      setItemId(null);
      setCallTable(false);
      const documentTitle = `${f({ id: 'table.add' })} - ${f({ id: 'menu.autoReports' })} - ${f({
        id: 'menu.reports',
      })}`;
      setDocumentTitle(documentTitle);
    } else if (params && pathname === `${URL_AUTO_REPORTS}/edit/${params.id}`) {
      setIsSubpageOpened(true);
      setItemId(Number(params.id));
      setCallTable(false);
      const documentTitle = `${f({ id: 'systemSettings.editBtn' })} - ${f({
        id: 'menu.autoReports',
      })} - ${f({ id: 'menu.reports' })}`;
      setDocumentTitle(documentTitle);
    }

    return () => {
      setIsSubpageOpened(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <div>
      <div
        className="col-12"
        style={{
          display: isSubpageOpened ? 'none' : '',
        }}
      >
        <div className="row section page-head">
          <h4>
            {`${f({ id: 'menu.reports' })} / ${f({ id: 'menu.autoReports' })}`}
            <OnlineHelp topic="auto-reports" />
          </h4>
        </div>
        <TabComponent tabs={tabs}>
          <div>
            <DynamicTable
              name={TABLE_AUTO_REPORT}
              mustClosed={true}
              updateTable={callTable}
              buttons={buttons[TABLE_AUTO_REPORT]}
              openForm={handleModal}
              routerParams={params}
            />
          </div>
        </TabComponent>
      </div>
      {(window.location.pathname === `${URL_AUTO_REPORTS}/add` ||
        window.location.pathname === `${URL_AUTO_REPORTS}/edit/${itemId}`) && (
        <div>
          <AllForms closeCallback={handleClose} editingItemId={itemId} updateTable={updateTable} />
        </div>
      )}
    </div>
  );
}

export default AutoReport;
